import { Modal } from 'antd';
import _ from 'lodash';
import ConditionDocumentApprovalStatusEnum from '~Api/Application/ConditionDocumentApprovalStatusEnum';
import ExtensionTypeEnum from '~Api/Application/ExtensionTypeEnum';
import IApplication from '~Api/Application/IApplication';
import ICondition from '~Api/Application/ICondition';
import IConditionDocument from '~Api/Application/IConditionDocument';
import MortgageTypeEnum from '~Api/Application/MortgageTypeEnum';
import IProperty from '~Api/Deal/IProperty';
import { IDealCalculatedAmounts } from '~Deals/utilities';
import { IDictionary } from '~utilities/IDictionary';
import React from 'react';
import { IWorkflowStatusMoveErrors } from './Application/IWorkflowStatusMoveErrors';
import InterestMethodEnum from '~Api/Application/InterestMethodEnum';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import { calculateInterestAmounts } from '~utilities/calculatorUtilities';

export function approvedConditionDocumentExists(conditionDocuments: IConditionDocument[]): boolean {
    return _.some(conditionDocuments, (conditionDocument: IConditionDocument) => (
        [ ConditionDocumentApprovalStatusEnum.Approved, ConditionDocumentApprovalStatusEnum.Legacy ].includes(conditionDocument.approvalStatus)
    ));
}

export function calculateNetPrepaidBalanceOnSettlement(application: IApplication, properties: IDictionary<IProperty>): number {
    const { interestPayable } = calculateInterestAmounts(
        application.loanAmount,
        application.interestRate,
        application.termMonths,
        InterestMethodEnum.InterestMethod365 === application.interestMethod,
    );

    // Ignore discharged properties for debt calculations
    const activeProperties: IProperty[] = _.filter(properties, (property: IProperty) => !property.dischargeTime);

    let totalCurrentDebt: number = 0;
    if (application.mortgageType === MortgageTypeEnum.FirstMortgage && application.extensionType !== ExtensionTypeEnum.Renewal) {
        totalCurrentDebt = _.round(_.sumBy(_.values(activeProperties), 'currentDebt') || 0, 2);
    } else if (application.extensionType === ExtensionTypeEnum.Renewal) {
        totalCurrentDebt = application.payoutFigure ? application.payoutFigure.balanceAmount : _.round(_.sumBy(_.values(activeProperties), 'currentDebt') || 0, 2);
    }

    return _.round(application.loanAmount - interestPayable - application.establishmentFee - (_.round(application.applicationFee * 1.1, 2)) - (_.round(application.brokerageFee * 1.1, 2)) - (_.round(application.legalFees * 1.1, 2)) - application.estimatedOutlays - totalCurrentDebt, 2);
}

export function sortApplicationConditions(a: ICondition, b: ICondition): number {
    // 0 = Application, 1 = Borrower, 2 = Property
    let groupA: number = 0;
    if (a.applicationBorrowerUuid) {
        groupA = 1;
    }
    if (a.applicationPropertyUuid) {
        groupA = 2;
    }

    let groupB: number = 0;
    if (b.applicationBorrowerUuid) {
        groupB = 1;
    }
    if (b.applicationPropertyUuid) {
        groupB = 2;
    }

    if (groupA !== groupB) {
        return groupA - groupB;
    }

    // compares by uuid to group
    if (a.applicationBorrowerUuid) {
        if (a.applicationBorrowerUuid !== b.applicationBorrowerUuid) {
            return a.applicationBorrowerUuid.localeCompare(b.applicationBorrowerUuid);
        }
    }

    if (a.applicationPropertyUuid) {
        if (a.applicationPropertyUuid !== b.applicationPropertyUuid) {
            return a.applicationPropertyUuid.localeCompare(b.applicationPropertyUuid);
        }
    }

    // Finally compare by name (case-insensitive)
    return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
}

export function showWorkflowStatusMoveErrorsModal(workflowStatusMoveErrors: IWorkflowStatusMoveErrors): void {
    const { informationErrors, documentErrors } = workflowStatusMoveErrors;
    const missingInformationBlock: JSX.Element = informationErrors.length > 0 && (
        <>
            <p>Missing Information</p>
            <ul>
                {informationErrors.map((error: string) => <li key={error}>{error}</li>)}
            </ul>
        </>
    );

    const missingDocumentsBlock: JSX.Element = documentErrors.length > 0 && (
        <>
            <p>Missing Documents</p>
            <ul>
                {documentErrors.map((error: string) => <li key={error}>{error}</li>)}
            </ul>
        </>
    );

    Modal.error({
        content: (
            <>
                {missingInformationBlock}
                {missingDocumentsBlock}
            </>
        ),
        title: 'Unable to Proceed',
        width: 600,
    });
}

export function getDealCalculatedAmountsFromApplication(application: IApplication): IDealCalculatedAmounts {
    const interestRateDefault: number = application.interestRateDefault || (application.interestRate + 10);

    const {
        annualInterest,
        interestPayable,
        monthlyInterest,
        monthlyInterestDefault,
    } = calculateInterestAmounts(
        application.loanAmount,
        application.interestRate,
        application.termMonths,
        InterestMethodEnum.InterestMethod365 === application.interestMethod,
        interestRateDefault,
    );

    const properties: IDictionary<IProperty> = {};
    let propertyValue: number = 0;
    let currentDebt: number = 0;

    _.forEach(application.properties, (property: IApplicationProperty) => {
        if (property.dealProperty.dischargeTime) {
            return;
        }

        properties[property.dealPropertyUuid] = property.dealProperty;
        propertyValue += property.valuationValue ?? property.dealProperty.estimatedValue;
        // Ignore property debt on extension applications
        currentDebt += application.isExtension ? 0 : property.dealProperty.currentDebt;
    });

    const grossBalanceOnSettlement: number = application.loanAmount - interestPayable - application.establishmentFee - application.applicationFee - application.brokerageFee - application.legalFees - application.estimatedOutlays;
    const netPrepaidBalanceOnSettlement: number = application.netBalance || calculateNetPrepaidBalanceOnSettlement(application, properties);

    const totalCostsDollars: number = application.loanAmount - netPrepaidBalanceOnSettlement;
    const totalCostsPercentage: number = totalCostsDollars / application.loanAmount * 100;
    const totalCostsPercentagePerAnnum: number = Number((application.establishmentFee + application.applicationFee + application.brokerageFee + application.legalFees + application.estimatedOutlays + annualInterest) / application.loanAmount * 100);

    const totalCurrentDebt: number = application.payoutFigure ? application.payoutFigure.balanceAmount : currentDebt;

    const lvr: number = _.round(((application.loanAmount + (application.mortgageType === MortgageTypeEnum.FirstMortgage ? 0 : totalCurrentDebt || 0)) / propertyValue * 100), 2);

    return {
        applicationFeeTotal: application.applicationFee,
        brokerageFeeTotal: application.brokerageFee,
        defaultInterestRate: interestRateDefault,
        establishmentFeeTotal: application.establishmentFee,
        grossBalanceOnSettlement,
        grossLoanAmount: application.loanAmount,
        interestPayable,
        lvr: lvr,
        monthlyInterestPayments: monthlyInterest,
        monthlyInterestPaymentsDefault: monthlyInterestDefault,
        netPrepaidBalanceOnSettlement,
        totalCostsDollars,
        totalCostsPercentage,
        totalCostsPercentagePerAnnum,
        totalCurrentDebt,
        totalValue: propertyValue,
    };
}
